import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, X, Smartphone, Share, Plus } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InstallPrompt() {
  const { i18n } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [hasUsedPwa, setHasUsedPwa] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [installStatus, setInstallStatus] = useState('idle'); // idle, prompting, success, error
  
  // Check if running as installed PWA
  const isStandalone = useMemo(() => {
    return window.matchMedia('(display-mode: standalone)').matches || 
           window.navigator.standalone === true;
  }, []);

  const isIOS = useMemo(() => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  }, []);

  const isAndroid = useMemo(() => {
    return /Android/.test(navigator.userAgent);
  }, []);

  const isBrave = useMemo(() => {
    return navigator.brave !== undefined || /Brave/.test(navigator.userAgent);
  }, []);

  const isChromium = useMemo(() => {
    return /Chrome/.test(navigator.userAgent) && !/Edg/.test(navigator.userAgent);
  }, []);

  // Register PWA usage when running in standalone mode
  const registerPwaUsage = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/register-pwa`, {}, { withCredentials: true });
      console.log('PWA usage registered');
    } catch (error) {
      console.log('Could not register PWA usage:', error.message);
    }
  }, []);

  // Check if user has used PWA before
  const checkPwaStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/pwa-status`, { withCredentials: true });
      setHasUsedPwa(response.data.has_used_pwa);
      setIsAuthenticated(true);
      return response.data.has_used_pwa;
    } catch (error) {
      setIsAuthenticated(false);
      return true;
    }
  }, []);

  useEffect(() => {
    if (isStandalone) {
      registerPwaUsage();
      return;
    }

    const initCheck = async () => {
      const hasPwa = await checkPwaStatus();
      
      if (hasPwa) {
        return;
      }

      const dismissedTime = localStorage.getItem('pwa-prompt-dismissed');
      if (dismissedTime) {
        const hoursSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60);
        if (hoursSinceDismissed < 24) {
          return;
        }
      }

      // Show prompt after delay for iOS or if no beforeinstallprompt
      setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
    };

    const handleBeforeInstallPrompt = (e) => {
      console.log('beforeinstallprompt event fired');
      e.preventDefault();
      setDeferredPrompt(e);
      
      if (hasUsedPwa === false) {
        setShowPrompt(true);
      }
    };

    const handleAppInstalled = () => {
      console.log('App installed successfully!');
      setInstallStatus('success');
      setShowPrompt(false);
      setDeferredPrompt(null);
      registerPwaUsage();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    initCheck();

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isStandalone, hasUsedPwa, registerPwaUsage, checkPwaStatus]);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      console.log('No install prompt available');
      return;
    }

    setInstallStatus('prompting');
    
    try {
      console.log('Showing install prompt...');
      await deferredPrompt.prompt();
      
      const { outcome } = await deferredPrompt.userChoice;
      console.log('User choice:', outcome);
      
      if (outcome === 'accepted') {
        setInstallStatus('success');
        setShowPrompt(false);
        registerPwaUsage();
      } else {
        setInstallStatus('idle');
      }
    } catch (error) {
      console.error('Install error:', error);
      setInstallStatus('error');
    }
    
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (isStandalone || !isAuthenticated || hasUsedPwa === true || hasUsedPwa === null || !showPrompt) {
    return null;
  }

  const messages = {
    es: {
      title: '¡Instala Motivator!',
      description: 'Añade la app a tu pantalla de inicio para acceso rápido sin necesidad de abrir el navegador.',
      install: 'Instalar App',
      installing: 'Instalando...',
      iosTitle: 'Añadir a pantalla de inicio',
      iosDescription: 'Para instalar Motivator en tu dispositivo:',
      iosStep1: 'Toca el botón compartir',
      iosStep2: 'Busca y toca "Añadir a pantalla de inicio"',
      iosStep3: 'Confirma tocando "Añadir"',
      braveInstructions: 'En Brave: Abre el menú (☰ o ⋮) → "Instalar aplicación" o "Añadir a pantalla de inicio"',
      androidNoPrompt: 'Abre el menú del navegador (⋮) y selecciona "Instalar aplicación" o "Añadir a pantalla de inicio"',
      cantInstall: 'Tu navegador no soporta instalación automática.',
      manualSteps: 'Pasos para instalar manualmente:'
    },
    en: {
      title: 'Install Motivator!',
      description: 'Add the app to your home screen for quick access without opening the browser.',
      install: 'Install App',
      installing: 'Installing...',
      iosTitle: 'Add to Home Screen',
      iosDescription: 'To install Motivator on your device:',
      iosStep1: 'Tap the share button',
      iosStep2: 'Find and tap "Add to Home Screen"',
      iosStep3: 'Confirm by tapping "Add"',
      braveInstructions: 'In Brave: Open menu (☰ or ⋮) → "Install app" or "Add to Home Screen"',
      androidNoPrompt: 'Open browser menu (⋮) and select "Install app" or "Add to Home Screen"',
      cantInstall: 'Your browser does not support automatic installation.',
      manualSteps: 'Steps to install manually:'
    },
    gl: {
      title: 'Instala Motivator!',
      description: 'Engade a app á túa pantalla de inicio para acceso rápido.',
      install: 'Instalar App',
      installing: 'Instalando...',
      iosTitle: 'Engadir á pantalla de inicio',
      iosDescription: 'Para instalar Motivator no teu dispositivo:',
      iosStep1: 'Toca o botón compartir',
      iosStep2: 'Busca e toca "Engadir á pantalla de inicio"',
      iosStep3: 'Confirma tocando "Engadir"',
      braveInstructions: 'En Brave: Abre o menú (☰ ou ⋮) → "Instalar aplicación"',
      androidNoPrompt: 'Abre o menú do navegador (⋮) e selecciona "Instalar aplicación"',
      cantInstall: 'O teu navegador non soporta instalación automática.',
      manualSteps: 'Pasos para instalar manualmente:'
    },
    ca: {
      title: 'Instal·la Motivator!',
      description: 'Afegeix l\'app a la teva pantalla d\'inici per accés ràpid.',
      install: 'Instal·lar App',
      installing: 'Instal·lant...',
      iosTitle: 'Afegir a pantalla d\'inici',
      iosDescription: 'Per instal·lar Motivator al teu dispositiu:',
      iosStep1: 'Toca el botó compartir',
      iosStep2: 'Busca i toca "Afegir a pantalla d\'inici"',
      iosStep3: 'Confirma tocant "Afegir"',
      braveInstructions: 'A Brave: Obre el menú (☰ o ⋮) → "Instal·lar aplicació"',
      androidNoPrompt: 'Obre el menú del navegador (⋮) i selecciona "Instal·lar aplicació"',
      cantInstall: 'El teu navegador no suporta instal·lació automàtica.',
      manualSteps: 'Passos per instal·lar manualment:'
    },
    zh: {
      title: '安装 Motivator！',
      description: '将应用添加到主屏幕，无需打开浏览器即可快速访问。',
      install: '安装应用',
      installing: '安装中...',
      iosTitle: '添加到主屏幕',
      iosDescription: '要在您的设备上安装 Motivator：',
      iosStep1: '点击分享按钮',
      iosStep2: '找到并点击"添加到主屏幕"',
      iosStep3: '点击"添加"确认',
      braveInstructions: '在 Brave 中：打开菜单 (☰ 或 ⋮) → "安装应用"',
      androidNoPrompt: '打开浏览器菜单(⋮)，选择"安装应用"或"添加到主屏幕"',
      cantInstall: '您的浏览器不支持自动安装。',
      manualSteps: '手动安装步骤：'
    }
  };

  const lang = i18n.language || 'es';
  const msg = messages[lang] || messages.es;

  // iOS specific UI
  if (isIOS) {
    return (
      <div className="fixed bottom-20 left-3 right-3 sm:left-auto sm:right-4 sm:w-96 z-50 animate-slide-up">
        <div className="bg-white dark:bg-zinc-800 border-2 border-[#18181B] dark:border-zinc-600 rounded-2xl hard-shadow p-4 relative">
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
          >
            <X className="w-4 h-4 text-zinc-900 dark:text-white" />
          </button>
          
          <div className="pr-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl bg-[#FF8BA7] border-2 border-[#18181B] dark:border-zinc-600 flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-6 h-6 text-[#18181B]" />
              </div>
              <div>
                <h3 className="font-bold text-sm text-zinc-900 dark:text-white">{msg.iosTitle}</h3>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">{msg.iosDescription}</p>
              </div>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-3 p-2 bg-zinc-50 dark:bg-zinc-700 rounded-lg">
                <div className="w-8 h-8 rounded-lg bg-[#007AFF] flex items-center justify-center flex-shrink-0">
                  <Share className="w-4 h-4 text-white" />
                </div>
                <span className="text-xs text-zinc-900 dark:text-white">{msg.iosStep1}</span>
              </div>
              
              <div className="flex items-center gap-3 p-2 bg-zinc-50 dark:bg-zinc-700 rounded-lg">
                <div className="w-8 h-8 rounded-lg bg-zinc-200 dark:bg-zinc-600 flex items-center justify-center flex-shrink-0">
                  <Plus className="w-4 h-4 text-zinc-700 dark:text-white" />
                </div>
                <span className="text-xs text-zinc-900 dark:text-white">{msg.iosStep2}</span>
              </div>
              
              <div className="flex items-center gap-3 p-2 bg-[#ADE498]/30 dark:bg-[#ADE498]/20 rounded-lg border border-[#ADE498]">
                <div className="w-8 h-8 rounded-lg bg-[#ADE498] border-2 border-[#18181B] dark:border-zinc-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-[#18181B]">✓</span>
                </div>
                <span className="text-xs font-medium text-zinc-900 dark:text-white">{msg.iosStep3}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Android/Desktop/Brave UI
  return (
    <div className="fixed bottom-20 left-3 right-3 sm:left-auto sm:right-4 sm:w-96 z-50 animate-slide-up">
      <div className="bg-white dark:bg-zinc-800 border-2 border-[#18181B] dark:border-zinc-600 rounded-2xl hard-shadow p-4 relative">
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
        >
          <X className="w-4 h-4 text-zinc-900 dark:text-white" />
        </button>
        
        <div className="pr-8">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-[#FF8BA7] border-2 border-[#18181B] dark:border-zinc-600 flex items-center justify-center flex-shrink-0">
              <Download className="w-6 h-6 text-[#18181B]" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-sm mb-1 text-zinc-900 dark:text-white">{msg.title}</h3>
              <p className="text-xs text-zinc-600 dark:text-zinc-400">{msg.description}</p>
            </div>
          </div>
          
          {deferredPrompt ? (
            <button
              onClick={handleInstall}
              disabled={installStatus === 'prompting'}
              className="w-full rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-[#ADE498] py-2.5 text-sm font-bold text-[#18181B] hover:bg-[#bdeaa8] hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] active:translate-y-1 active:shadow-none transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {installStatus === 'prompting' ? msg.installing : msg.install}
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-xs font-medium text-zinc-700 dark:text-zinc-300">{msg.manualSteps}</p>
              <div className="p-3 bg-zinc-50 dark:bg-zinc-700 rounded-xl border border-zinc-200 dark:border-zinc-600">
                <p className="text-xs text-zinc-600 dark:text-zinc-400">
                  {isBrave ? msg.braveInstructions : (isChromium ? msg.androidNoPrompt : msg.cantInstall)}
                </p>
              </div>
              {(isBrave || isChromium) && (
                <div className="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
                  <span className="inline-flex items-center justify-center w-5 h-5 bg-zinc-200 dark:bg-zinc-600 rounded text-[10px] font-bold text-zinc-900 dark:text-white">⋮</span>
                  <span>→</span>
                  <span className="px-2 py-1 bg-[#ADE498]/30 dark:bg-[#ADE498]/20 rounded text-[10px] font-medium text-zinc-900 dark:text-white">Instalar aplicación</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
